import React, { useEffect, useMemo, useState } from "react";

export const HERO_COPY_TYPE_DELAY_MS = 1520;

function normalizeText(text, separator) {
  if (Array.isArray(text)) {
    return text.filter(Boolean).join(separator);
  }

  return text || "";
}

function getPrefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    getPrefersReducedMotion
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener?.("change", handleChange);
    return () => mediaQuery.removeEventListener?.("change", handleChange);
  }, []);

  return prefersReducedMotion;
}

export default function HeroTypewriterText({
  text,
  separator = "\n\n",
  initialDelayMs = HERO_COPY_TYPE_DELAY_MS,
  typingSpeedMs = 7,
  className,
  ...props
}) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const fullText = useMemo(() => normalizeText(text, separator), [separator, text]);
  const [displayedText, setDisplayedText] = useState(() =>
    prefersReducedMotion ? fullText : ""
  );
  const [hasStarted, setHasStarted] = useState(() => prefersReducedMotion);
  const [isComplete, setIsComplete] = useState(() => prefersReducedMotion);

  useEffect(() => {
    if (!fullText || prefersReducedMotion) {
      setDisplayedText(fullText);
      setHasStarted(true);
      setIsComplete(true);
      return undefined;
    }

    let timeoutId;
    let index = 0;
    const chars = Array.from(fullText);

    setDisplayedText("");
    setHasStarted(false);
    setIsComplete(false);

    const typeNextChar = () => {
      index += 1;
      setHasStarted(true);
      setDisplayedText(chars.slice(0, index).join(""));

      if (index >= chars.length) {
        setIsComplete(true);
        return;
      }

      timeoutId = window.setTimeout(typeNextChar, typingSpeedMs);
    };

    timeoutId = window.setTimeout(typeNextChar, initialDelayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [fullText, initialDelayMs, prefersReducedMotion, typingSpeedMs]);

  return (
    <p
      className={className}
      data-typewriter-text
      data-typewriter-started={hasStarted ? "true" : "false"}
      data-typewriter-complete={isComplete ? "true" : "false"}
      data-typewriter-delay={initialDelayMs}
      aria-label={fullText}
      {...props}
    >
      <span
        aria-hidden="true"
        data-typewriter-layer
        style={{
          position: "relative",
          display: "block",
          whiteSpace: "pre-wrap",
        }}
      >
        <span
          data-typewriter-measure
          data-typewriter-full={fullText}
          style={{
            display: "block",
            visibility: "hidden",
            whiteSpace: "pre-wrap",
          }}
        />
        <span
          data-typewriter-visible
          style={{
            position: "absolute",
            inset: 0,
            display: "block",
            whiteSpace: "pre-wrap",
          }}
        >
          {displayedText}
          {hasStarted && !isComplete ? (
            <span aria-hidden="true" data-typewriter-cursor />
          ) : null}
        </span>
      </span>
    </p>
  );
}
